/**
 * Inbox click targets: type + payload → SPA path (entity ids when present).
 * Invalid / empty / self / communication urls expand in place instead of 404.
 *
 * Other modules: registerInboxPathBuilder('type', (data) => '/path/' + data.uuid)
 */

const TYPE_ROUTES = {
    homework: '/resource/home-work',
    exam: '/exam/schedule',
    fee: '/finance/fee',
    event: '/calendar/event',
    enquiry: '/reception/enquiry',
    leave: '/employee/leave/request',
    attendance: '/student/attendance',
    timetable: '/academic/timetable',
    inventory: '/inventory/stock/item',
    transport: '/transport/route',
    hostel: '/hostel/rooms',
    behaviour: '/behaviour/assign',
    payroll: '/employee/payroll/list',
    promotion: '/student/promotion',
    feed: '/post/feed',
    profile: '/student',
    complaint: '/reception/complaint'
};

const URL_ALIASES = {
    '/finance/transaction': '/finance/fee',
    '/resource/homework': '/resource/home-work',
    '/student/notifications': '',
    '/student/admission': '/student/registration',
    '/dashboard': '/home',
    '/notification': ''
};

const STAY_PATHS = {
    '/communication/notification': true
};

const STAY_TYPES = {
    communication: true,
    email: true,
    sms: true,
    whatsapp: true,
    push_notification: true
};

const ALLOWED_PREFIXES = [
    '/home',
    '/student',
    '/employee',
    '/finance',
    '/exam',
    '/resource',
    '/calendar',
    '/reception',
    '/communication',
    '/inventory',
    '/library',
    '/transport',
    '/hostel',
    '/academic',
    '/configuration',
    '/utility',
    '/post',
    '/meet',
    '/behaviour',
    '/download-center',
    '/institute',
    '/online-exam',
    '/lesson-plan',
    '/reports',
    '/qr',
    '/setup',
    '/frontend',
    '/change/password'
];

const pathBuilders = {};

function firstValue(data, keys) {
    if (!data) {
        return '';
    }
    for (let i = 0; i < keys.length; i++) {
        const value = data[keys[i]];
        if (value !== undefined && value !== null && String(value) !== '') {
            return String(value);
        }
    }
    return '';
}

pathBuilders.fee = function (data) {
    const studentUuid = firstValue(data, ['student_uuid']);
    const recordId = firstValue(data, ['student_record_id', 'record_id']);
    if (studentUuid && recordId) {
        return '/student/' + studentUuid + '/fee/' + recordId;
    }
    return '';
};

pathBuilders.enquiry = function (data) {
    const uuid = firstValue(data, ['enquiry_uuid', 'uuid']);
    return uuid ? '/reception/enquiry/' + uuid : '';
};

pathBuilders.leave = function (data) {
    const uuid = firstValue(data, ['leave_uuid', 'uuid']);
    return uuid ? '/employee/leave/request/' + uuid : '';
};

pathBuilders.homework = function (data) {
    const uuid = firstValue(data, ['homework_uuid', 'home_work_uuid', 'uuid']);
    return uuid ? '/resource/home-work/' + uuid : '';
};

pathBuilders.event = function (data) {
    return firstValue(data, ['event_uuid', 'uuid']) ? '/calendar/event' : '';
};

pathBuilders.exam = function (data) {
    return firstValue(data, ['exam_schedule_id', 'exam_uuid', 'id']) ? '/exam/schedule' : '';
};

export function registerInboxPathBuilder(type, builder) {
    if (!type || typeof builder !== 'function') {
        return;
    }
    pathBuilders[String(type).toLowerCase()] = builder;
}

function typeKey(item) {
    if (!item) {
        return '';
    }
    const raw = item.type || item.trigger || item.action || '';
    return String(raw).toLowerCase().replace(/\s+/g, '_');
}

export function parseItemData(item) {
    if (!item) {
        return {};
    }
    let data = item.data;
    if (typeof data === 'string') {
        try {
            data = JSON.parse(data);
        } catch (e) {
            data = {};
        }
    }
    if (!data || typeof data !== 'object' || Array.isArray(data)) {
        return {};
    }
    return data;
}

export function entityIdFor(type, data) {
    const key = String(type || '').toLowerCase();
    const payload = data || {};
    if (key === 'fee') {
        return firstValue(payload, ['student_uuid', 'student_record_id', 'record_id']);
    }
    if (key === 'enquiry') {
        return firstValue(payload, ['enquiry_uuid', 'uuid']);
    }
    if (key === 'leave') {
        return firstValue(payload, ['leave_uuid', 'uuid']);
    }
    if (key === 'homework') {
        return firstValue(payload, ['homework_uuid', 'home_work_uuid', 'uuid']);
    }
    if (key === 'event') {
        return firstValue(payload, ['event_uuid', 'uuid']);
    }
    if (key === 'exam') {
        return firstValue(payload, ['exam_schedule_id', 'exam_uuid', 'id']);
    }
    return firstValue(payload, ['uuid', 'id']);
}

export function entityIdFromItem(item) {
    const data = parseItemData(item);
    let id = entityIdFor(typeKey(item), data);
    if (id) {
        return id;
    }
    const path = pathOnly(normalizePath(item && item.url));
    const parts = path.split('/').filter(Boolean);
    if (!parts.length) {
        return '';
    }
    const last = parts[parts.length - 1];
    if (last === 'edit' || last === 'create' || last === 'set') {
        return parts.length > 1 ? parts[parts.length - 2] : '';
    }
    if (parts[0] === 'student' && parts[2] === 'fee' && parts[1]) {
        return parts[1];
    }
    if (last && last !== parts[0] && TYPE_ROUTES[typeKey(item)] !== path) {
        return last;
    }
    return '';
}

export function buildInboxPath(type, payload) {
    const key = String(type || '').toLowerCase();
    if (STAY_TYPES[key]) {
        return '';
    }
    const data = payload && typeof payload === 'object' && !Array.isArray(payload)
        ? (payload.data && typeof payload.data === 'object' ? payload.data : payload)
        : {};
    const builder = pathBuilders[key];
    if (builder) {
        const built = builder(data, key) || '';
        if (built) {
            return normalizePath(built);
        }
    }
    return TYPE_ROUTES[key] || '';
}

function looksLikeJson(value) {
    return value.charAt(0) === '{' || value.charAt(0) === '[';
}

function normalizePath(raw) {
    if (raw == null) {
        return '';
    }
    if (typeof raw === 'object') {
        return '';
    }
    let value = String(raw).trim();
    if (!value || value === '#' || value === '/' || value === 'null' || value === 'undefined') {
        return '';
    }
    if (looksLikeJson(value)) {
        return '';
    }

    const hashIdx = value.indexOf('#');
    if (hashIdx >= 0) {
        const after = value.slice(hashIdx + 1);
        if (after.charAt(0) === '/') {
            value = after;
        } else if (after) {
            value = '/' + after.replace(/^\/+/, '');
        } else {
            value = value.slice(0, hashIdx);
        }
    }

    if (/^https?:\/\//i.test(value)) {
        try {
            const parsed = new URL(value);
            if (typeof window !== 'undefined' && parsed.origin !== window.location.origin) {
                return '';
            }
            value = parsed.pathname || '';
            if (parsed.search) {
                value += parsed.search;
            }
        } catch (e) {
            return '';
        }
    }

    if (!value || value.charAt(0) !== '/') {
        if (value && /^[A-Za-z0-9]/.test(value) && value.indexOf(' ') === -1) {
            value = '/' + value;
        } else {
            return '';
        }
    }

    const qIdx = value.indexOf('?');
    let path = qIdx >= 0 ? value.slice(0, qIdx) : value;
    const query = qIdx >= 0 ? value.slice(qIdx) : '';
    if (path.length > 1) {
        path = path.replace(/\/+$/, '');
    }

    if (Object.prototype.hasOwnProperty.call(URL_ALIASES, path)) {
        const mapped = URL_ALIASES[path];
        if (!mapped) {
            return '';
        }
        path = mapped;
    }

    return path + query;
}

function pathOnly(path) {
    return String(path || '').split('?')[0];
}

function hasAllowedPrefix(path) {
    const base = pathOnly(path);
    for (let i = 0; i < ALLOWED_PREFIXES.length; i++) {
        const prefix = ALLOWED_PREFIXES[i];
        if (base === prefix || base.indexOf(prefix + '/') === 0) {
            return true;
        }
    }
    return false;
}

function isCatchAllRecord(record) {
    if (!record) {
        return true;
    }
    const p = record.path;
    return p === '*' || p === '/:pathMatch(.*)*' || p === '/:pathMatch(.*)';
}

export function routeExists(router, path) {
    if (!router || !path) {
        return false;
    }
    try {
        const resolved = router.resolve(path);
        const route = resolved.route || resolved.resolved || resolved;
        const matched = (route && route.matched) || [];
        if (!matched.length) {
            return false;
        }
        return !isCatchAllRecord(matched[matched.length - 1]);
    } catch (e) {
        return false;
    }
}

function isNavigable(router, path) {
    if (!path || STAY_PATHS[pathOnly(path)]) {
        return false;
    }
    if (!hasAllowedPrefix(path)) {
        return false;
    }
    if (router) {
        return routeExists(router, path);
    }
    return true;
}

function typeRouteFor(item) {
    const type = typeKey(item);
    if (TYPE_ROUTES[type]) {
        return TYPE_ROUTES[type];
    }
    const keys = Object.keys(TYPE_ROUTES);
    for (let i = 0; i < keys.length; i++) {
        if (type.indexOf(keys[i]) !== -1) {
            return TYPE_ROUTES[keys[i]];
        }
    }
    return '';
}

export function withHighlight(path, id) {
    if (!path || id === undefined || id === null || id === '') {
        return path;
    }
    if (/[?&]highlight=/.test(path)) {
        return path;
    }
    const sep = path.indexOf('?') >= 0 ? '&' : '?';
    return path + sep + 'highlight=' + encodeURIComponent(String(id));
}

export function getHighlightId(route) {
    if (!route) {
        return '';
    }
    if (route.query && route.query.highlight) {
        return String(route.query.highlight);
    }
    if (route.hash && route.hash.length > 1) {
        try {
            return decodeURIComponent(route.hash.replace(/^#/, ''));
        } catch (e) {
            return route.hash.replace(/^#/, '');
        }
    }
    return '';
}

export function clearHighlightQuery(router, route) {
    if (!router || !route) {
        return;
    }
    const hasQuery = route.query && route.query.highlight != null && route.query.highlight !== '';
    const hasHash = route.hash && route.hash.length > 1;
    if (!hasQuery && !hasHash) {
        return;
    }
    const query = Object.assign({}, route.query || {});
    delete query.highlight;
    router.replace({ path: route.path, query: query, hash: '' }).catch(function () {});
}

function cssEscape(value) {
    const str = String(value);
    if (typeof window !== 'undefined' && window.CSS && typeof window.CSS.escape === 'function') {
        return window.CSS.escape(str);
    }
    return str.replace(/["\\]/g, '\\$&');
}

export function pulseHighlight(el, duration) {
    if (!el || !el.classList) {
        return;
    }
    el.classList.add('sip-inbox-highlight');
    try {
        if (typeof el.scrollIntoView === 'function') {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    } catch (e) {}
    const ms = duration || 2000;
    setTimeout(function () {
        el.classList.remove('sip-inbox-highlight');
    }, ms);
}

export function applyRouteHighlight(vm, options) {
    if (!vm || !vm.$route) {
        return;
    }
    const opts = options || {};
    const id = getHighlightId(vm.$route);
    if (!id) {
        return;
    }

    const run = function () {
        const root = vm.$el;
        let el = null;
        if (root && root.querySelector) {
            el = root.querySelector('[data-inbox-id="' + cssEscape(id) + '"]');
            if (!el && opts.fallback) {
                el = root.querySelector(opts.fallback);
            }
        }
        if (el) {
            pulseHighlight(el);
            if (typeof opts.onFound === 'function') {
                opts.onFound(id, el);
            }
            clearHighlightQuery(vm.$router, vm.$route);
            return;
        }
        if (typeof opts.onMissing === 'function') {
            opts.onMissing(id);
            if (opts.clearOnMissing !== false) {
                clearHighlightQuery(vm.$router, vm.$route);
            }
            return;
        }
        clearHighlightQuery(vm.$router, vm.$route);
    };

    if (vm.$nextTick) {
        vm.$nextTick(run);
    } else {
        setTimeout(run, 0);
    }
}

export function resolveInboxPath(item, router, currentPath) {
    if (STAY_TYPES[typeKey(item)]) {
        return null;
    }

    const data = parseItemData(item);
    const entityPath = buildInboxPath(typeKey(item), data);
    let path = entityPath || normalizePath(item && item.url);

    if (!path) {
        const actionPath = normalizePath(item && item.action);
        const mappedAction = typeRouteFor({ type: item && item.action });
        if (mappedAction) {
            path = mappedAction;
        } else if (actionPath && actionPath.split('/').filter(Boolean).length > 1) {
            path = actionPath;
        }
    }

    if (!isNavigable(router, path)) {
        path = typeRouteFor(item);
    }

    if (!isNavigable(router, path)) {
        return null;
    }

    const highlightId = entityIdFromItem(item);
    path = withHighlight(path, highlightId);

    if (currentPath && pathOnly(path) === currentPath && !highlightId) {
        return null;
    }

    return path;
}

export function navigateInboxItem(router, item, currentPath) {
    const path = resolveInboxPath(item, router, currentPath);
    if (!path || !router) {
        return false;
    }
    router.push(path).catch(function () {});
    return true;
}
